/**
 * 지출관리 (품의·원인행위·결의·지급)
 * - API(isBdApiEnabled): BdExpense · 아니면 localStorage
 * - 키: erp.expense.{proposal|obligation|resolution|payment}|{tenant}|{fy}
 * - 결의서 양식 필드맵: expense/resolutionFormFieldMap.js · BD-DESIGN-015
 */

import proposalSeed2026 from './seeds/proposalSeed2026';
import { toStorageDate } from 'utils/datePickerUtils';
import {
  isBdApiEnabled,
  apiLoadProposalBundle,
  apiSaveProposalBundle,
  setCachedProposalBundle,
  apiLoadObligationBundle,
  apiSaveObligationBundle,
  setCachedObligationBundle,
  apiLoadResolutionBundle,
  apiSaveResolutionBundle,
  setCachedResolutionBundle,
  apiLoadPayments,
  apiSavePayments,
  setCachedPayments,
} from './expenseApi';

export const PROPOSAL_STORAGE_PREFIX = 'erp.expense.proposal';
export const OBLIGATION_STORAGE_PREFIX = 'erp.expense.obligation';
export const RESOLUTION_STORAGE_PREFIX = 'erp.expense.resolution';
export const PAYMENT_STORAGE_PREFIX = 'erp.expense.payment';

/** 품의유형 */
export const PROPOSAL_TYPE = {
  GENERAL: 'GENERAL',
  CONSTRUCT: 'CONSTRUCT',
  SERVICE: 'SERVICE',
  GOODS: 'GOODS',
  OTHER: 'OTHER',
};

export const PROPOSAL_TYPE_OPTIONS = [
  { value: PROPOSAL_TYPE.GENERAL, label: '일반' },
  { value: PROPOSAL_TYPE.CONSTRUCT, label: '공사' },
  { value: PROPOSAL_TYPE.SERVICE, label: '용역' },
  { value: PROPOSAL_TYPE.GOODS, label: '물품' },
  { value: PROPOSAL_TYPE.OTHER, label: '기타/사업소득' },
];

/** 원인행위유형 */
export const OBLIGATION_TYPE = {
  GENERAL: 'GENERAL',
  CONSTRUCT: 'CONSTRUCT',
  SERVICE: 'SERVICE',
  GOODS: 'GOODS',
  OTHER: 'OTHER',
};

export const OBLIGATION_TYPE_OPTIONS = [
  { value: OBLIGATION_TYPE.GENERAL, label: '일반' },
  { value: OBLIGATION_TYPE.CONSTRUCT, label: '공사' },
  { value: OBLIGATION_TYPE.SERVICE, label: '용역' },
  { value: OBLIGATION_TYPE.GOODS, label: '물품' },
  { value: OBLIGATION_TYPE.OTHER, label: '기타' },
];

/** 결의유형 */
export const RESOLUTION_TYPE = {
  GENERAL: 'GENERAL',
  CONSTRUCT: 'CONSTRUCT',
  SERVICE: 'SERVICE',
  GOODS: 'GOODS',
  OTHER: 'OTHER',
};

export const RESOLUTION_TYPE_OPTIONS = [
  { value: RESOLUTION_TYPE.GENERAL, label: '일반' },
  { value: RESOLUTION_TYPE.CONSTRUCT, label: '공사' },
  { value: RESOLUTION_TYPE.SERVICE, label: '용역' },
  { value: RESOLUTION_TYPE.GOODS, label: '물품' },
  { value: RESOLUTION_TYPE.OTHER, label: '기타' },
];

export const EXPENSE_STATUS = {
  DRAFT: 'DRAFT',
  REQUESTED: 'REQUESTED',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
  CONFIRMED: 'CONFIRMED',
};

export const EXPENSE_STATUS_OPTIONS = [
  { value: EXPENSE_STATUS.DRAFT, label: '작성중' },
  { value: EXPENSE_STATUS.REQUESTED, label: '승인요청' },
  { value: EXPENSE_STATUS.APPROVED, label: '승인' },
  { value: EXPENSE_STATUS.REJECTED, label: '반려' },
  { value: EXPENSE_STATUS.CONFIRMED, label: '확정' },
];

/** 원인행위 확정/승인 모드 (기관 환경설정) */
export const OBLIGATION_CONFIRM_MODE = {
  CONFIRM: 'CONFIRM',
  APPROVE: 'APPROVE',
};

export const OBLIGATION_CONFIRM_MODE_OPTIONS = [
  { value: OBLIGATION_CONFIRM_MODE.CONFIRM, label: '확정 (승인 없이 확정)' },
  { value: OBLIGATION_CONFIRM_MODE.APPROVE, label: '승인 (승인요청→승인)' },
];

/** 회계처리 모드 */
export const ACCOUNTING_MODE = {
  MATCHED: 'MATCHED',
  SEPARATED: 'SEPARATED',
};

export const ACCOUNTING_MODE_OPTIONS = [
  { value: ACCOUNTING_MODE.MATCHED, label: '일치 (원인행위·결의 동시)' },
  { value: ACCOUNTING_MODE.SEPARATED, label: '분리 (원인행위·결의 별도)' },
];

export const ALLOW_NEGATIVE_EXEC_OPTIONS = [
  { value: 'N', label: 'N (마이너스 집행 불가)' },
  { value: 'Y', label: 'Y (마이너스 집행 허용)' },
];

export function todayYmd(d = new Date()) {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export function createUid(prefix = 'EXP') {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
}

export function createProposalUid() {
  return createUid('PRP');
}

export function createProposalDetailUid() {
  return createUid('PRPD');
}

export function createObligationUid() {
  return createUid('OBL');
}

export function createObligationDetailUid() {
  return createUid('OBLD');
}

export function createContractUid() {
  return createUid('CTR');
}

export function createResolutionUid() {
  return createUid('RES');
}

export function createResolutionDetailUid() {
  return createUid('RESD');
}

export function createPaymentUid() {
  return createUid('PAY');
}

function labelOf(options, code) {
  return options.find((o) => o.value === code)?.label || code || '';
}

export function proposalTypeLabel(code) {
  return labelOf(PROPOSAL_TYPE_OPTIONS, code);
}

export function obligationTypeLabel(code) {
  return labelOf(OBLIGATION_TYPE_OPTIONS, code);
}

export function resolutionTypeLabel(code) {
  return labelOf(RESOLUTION_TYPE_OPTIONS, code);
}

export function expenseStatusLabel(code) {
  return labelOf(EXPENSE_STATUS_OPTIONS, code);
}

function storageKey(prefix, dims = {}) {
  return [prefix, String(dims.tenantCode || '').trim(), String(dims.fiscalYear || '').trim()].join('|');
}

function readBundle(key, detailKeys = ['details']) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) {
      const empty = { masters: [] };
      detailKeys.forEach((k) => {
        empty[k] = [];
      });
      return empty;
    }
    const data = JSON.parse(raw);
    const out = { masters: Array.isArray(data?.masters) ? data.masters : [] };
    detailKeys.forEach((k) => {
      out[k] = Array.isArray(data?.[k]) ? data[k] : [];
    });
    return out;
  } catch {
    const empty = { masters: [] };
    detailKeys.forEach((k) => {
      empty[k] = [];
    });
    return empty;
  }
}

function writeBundle(key, payload) {
  localStorage.setItem(
    key,
    JSON.stringify({
      ...payload,
      savedAt: new Date().toISOString(),
    })
  );
}

// ─── Proposal (지출품의) ─────────────────────────────────────────

export function emptyProposalMaster(partial = {}) {
  return {
    proposalUid: '',
    proposalNo: '',
    fiscalYear: '',
    deptCode: '',
    deptName: '',
    projectCode: '',
    projectName: '',
    subjectCode: '',
    subjectName: '',
    fundCode: '',
    fundName: '',
    balanceUid: '',
    allocAmt: 0,
    proposalAmt: 0,
    proposalDate: '',
    remark: '',
    proposalType: PROPOSAL_TYPE.GENERAL,
    status: EXPENSE_STATUS.DRAFT,
    registerDate: '',
    approveDate: '',
    requestAt: '',
    mod: '',
    ...partial,
  };
}

export function emptyProposalVendorRow(partial = {}) {
  return {
    detailUid: '',
    proposalUid: '',
    vendorCode: '',
    vendorName: '',
    businessNo: '',
    amount: 0,
    remark: '',
    mod: '',
    ...partial,
  };
}

export async function loadProposalBundle(dims) {
  if (isBdApiEnabled()) return apiLoadProposalBundle(dims);
  const bundle = readBundle(storageKey(PROPOSAL_STORAGE_PREFIX, dims), ['vendors']);
  setCachedProposalBundle(dims, bundle);
  return bundle;
}

export async function saveProposalBundle(dims, masters, vendors) {
  if (isBdApiEnabled()) {
    return apiSaveProposalBundle(
      dims,
      masters,
      vendors,
      createProposalUid,
      createProposalDetailUid
    );
  }
  writeBundle(storageKey(PROPOSAL_STORAGE_PREFIX, dims), { masters, vendors });
  const bundle = await loadProposalBundle(dims);
  return bundle;
}

/**
 * Excel 품의내역(2026) 시드를 localStorage에 병합 삽입.
 * API 모드에서는 DB만 사용(시드 생략).
 */
export async function ensureProposalSeedImported(dims = {}) {
  if (isBdApiEnabled()) return loadProposalBundle(dims);
  const fy = String(dims.fiscalYear || '').trim();
  if (fy !== String(proposalSeed2026.fiscalYear || '2026')) {
    return loadProposalBundle(dims);
  }
  const bundle = await loadProposalBundle(dims);
  const existing = new Set((bundle.masters || []).map((m) => m.proposalUid));
  const toAddM = (proposalSeed2026.masters || []).filter((m) => m.proposalUid && !existing.has(m.proposalUid));
  if (!toAddM.length) return bundle;
  const addUids = new Set(toAddM.map((m) => m.proposalUid));
  const toAddV = (proposalSeed2026.vendors || []).filter((v) => addUids.has(v.proposalUid));
  return saveProposalBundle(
    dims,
    [...(bundle.masters || []), ...toAddM],
    [...(bundle.vendors || []), ...toAddV]
  );
}

export function nextProposalNo(masters = []) {
  const nums = (masters || [])
    .map((m) => Number(String(m.proposalNo || '').replace(/\D/g, '')))
    .filter((n) => Number.isFinite(n) && n > 0);
  const max = nums.length ? Math.max(...nums) : 0;
  return String(max + 1).padStart(4, '0');
}

/** 다중 선택 조회조건 — 콤마 구분 문자열을 소문자 Set으로 */
function toFilterSet(joined) {
  return new Set(
    String(joined || '')
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean)
  );
}

/**
 * 코드 우선 · 코드 없는 레거시(엑셀 이관) 건은 명칭으로 대체 매칭.
 * 선택이 1건이고 코드가 없으면 부분 일치(직접 입력 대비).
 */
function matchAxis(list, { codeField, nameField, codeJoined, nameJoined }) {
  const codes = toFilterSet(codeJoined);
  const names = toFilterSet(nameJoined);
  if (!codes.size && !names.size) return list;

  if (!codes.size && names.size === 1) {
    const [only] = [...names];
    return list.filter((m) => String(m[nameField] || '').toLowerCase().includes(only));
  }

  return list.filter((m) => {
    const code = String(m[codeField] || '').trim().toLowerCase();
    if (code && codes.size) return codes.has(code);
    return names.has(String(m[nameField] || '').trim().toLowerCase());
  });
}

export function filterProposalsForRegister(masters = [], filter = {}) {
  let list = [...(masters || [])];
  const st = String(filter.status || '').trim();
  if (st) list = list.filter((m) => m.status === st);

  list = matchAxis(list, {
    codeField: 'deptCode',
    nameField: 'deptName',
    codeJoined: filter.deptCode,
    nameJoined: filter.deptName,
  });
  list = matchAxis(list, {
    codeField: 'projectCode',
    nameField: 'projectName',
    codeJoined: filter.projectCode,
    nameJoined: filter.projectName,
  });
  list = matchAxis(list, {
    codeField: 'subjectCode',
    nameField: 'subjectName',
    codeJoined: filter.subjectCode,
    nameJoined: filter.subjectName,
  });

  const dateFrom = toStorageDate(filter.dateFrom);
  const dateTo = toStorageDate(filter.dateTo);
  if (dateFrom || dateTo) {
    list = list.filter((m) => {
      const d = toStorageDate(m.proposalDate);
      if (!d) return false;
      if (dateFrom && d < dateFrom) return false;
      if (dateTo && d > dateTo) return false;
      return true;
    });
  }

  const remark = String(filter.remark || '').trim().toLowerCase();
  if (remark) {
    list = list.filter((m) => String(m.remark || '').toLowerCase().includes(remark));
  }

  return list.filter((m) => m.mod !== 'D');
}

export function filterProposalsForApprove(masters = [], filter = {}) {
  const st = String(filter.status || '').trim();
  // 상태 미지정 시 승인요청만 (승인화면 기본)
  const withStatus = filterProposalsForRegister(masters, {
    ...filter,
    status: st || EXPENSE_STATUS.REQUESTED,
  });
  return withStatus;
}

export function requestProposalApproval(masters, vendors, proposalUid) {
  const uid = String(proposalUid || '').trim();
  const nextM = (masters || []).map((m) => {
    if (m.proposalUid !== uid) return m;
    return {
      ...m,
      status: EXPENSE_STATUS.REQUESTED,
      requestAt: new Date().toISOString(),
    };
  });
  return { masters: nextM, vendors: vendors || [] };
}

export function approveProposal(masters, vendors, proposalUid) {
  const uid = String(proposalUid || '').trim();
  const today = todayYmd();
  const nextM = (masters || []).map((m) => {
    if (m.proposalUid !== uid) return m;
    return {
      ...m,
      status: EXPENSE_STATUS.APPROVED,
      approveDate: today,
    };
  });
  return { masters: nextM, vendors: vendors || [] };
}

export function rejectProposal(masters, vendors, proposalUid, reason = '반려') {
  const uid = String(proposalUid || '').trim();
  const nextM = (masters || []).map((m) => {
    if (m.proposalUid !== uid) return m;
    return {
      ...m,
      status: EXPENSE_STATUS.REJECTED,
      rejectReason: reason,
    };
  });
  return { masters: nextM, vendors: vendors || [] };
}

// ─── Obligation (원인행위) ───────────────────────────────────────

export function emptyObligationMaster(partial = {}) {
  return {
    obligationUid: '',
    obligationNo: '',
    fiscalYear: '',
    deptCode: '',
    deptName: '',
    projectCode: '',
    projectName: '',
    subjectCode: '',
    subjectName: '',
    fundCode: '',
    fundName: '',
    balanceUid: '',
    proposalUid: '',
    proposalNo: '',
    obligationAmt: 0,
    obligationDate: '',
    remark: '',
    obligationType: OBLIGATION_TYPE.GENERAL,
    status: EXPENSE_STATUS.DRAFT,
    registerDate: '',
    approveDate: '',
    confirmDate: '',
    requestAt: '',
    mod: '',
    ...partial,
  };
}

export function emptyObligationVendorRow(partial = {}) {
  return {
    detailUid: '',
    obligationUid: '',
    vendorCode: '',
    vendorName: '',
    businessNo: '',
    amount: 0,
    remark: '',
    mod: '',
    ...partial,
  };
}

export function emptyContractRow(partial = {}) {
  return {
    contractUid: '',
    obligationUid: '',
    contractNo: '',
    contractName: '',
    contractDate: '',
    contractAmt: 0,
    vendorName: '',
    remark: '',
    mod: '',
    ...partial,
  };
}

export async function loadObligationBundle(dims) {
  if (isBdApiEnabled()) return apiLoadObligationBundle(dims);
  const bundle = readBundle(storageKey(OBLIGATION_STORAGE_PREFIX, dims), ['vendors', 'contracts']);
  setCachedObligationBundle(dims, bundle);
  return bundle;
}

export async function saveObligationBundle(dims, masters, vendors, contracts) {
  if (isBdApiEnabled()) {
    return apiSaveObligationBundle(
      dims,
      masters,
      vendors,
      contracts,
      createObligationUid,
      createObligationDetailUid,
      createContractUid
    );
  }
  writeBundle(storageKey(OBLIGATION_STORAGE_PREFIX, dims), { masters, vendors, contracts });
  return loadObligationBundle(dims);
}

export function nextObligationNo(masters = []) {
  const nums = (masters || [])
    .map((m) => Number(String(m.obligationNo || '').replace(/\D/g, '')))
    .filter((n) => Number.isFinite(n) && n > 0);
  const max = nums.length ? Math.max(...nums) : 0;
  return String(max + 1);
}

export function filterObligationsForRegister(masters = [], filter = {}) {
  let list = [...(masters || [])];
  const st = String(filter.status || '').trim();
  if (st) list = list.filter((m) => m.status === st);

  list = matchAxis(list, {
    codeField: 'deptCode',
    nameField: 'deptName',
    codeJoined: filter.deptCode,
    nameJoined: filter.deptName,
  });
  list = matchAxis(list, {
    codeField: 'projectCode',
    nameField: 'projectName',
    codeJoined: filter.projectCode,
    nameJoined: filter.projectName,
  });
  list = matchAxis(list, {
    codeField: 'subjectCode',
    nameField: 'subjectName',
    codeJoined: filter.subjectCode,
    nameJoined: filter.subjectName,
  });

  const dateFrom = toStorageDate(filter.dateFrom);
  const dateTo = toStorageDate(filter.dateTo);
  if (dateFrom || dateTo) {
    list = list.filter((m) => {
      const d = toStorageDate(m.obligationDate);
      if (!d) return false;
      if (dateFrom && d < dateFrom) return false;
      if (dateTo && d > dateTo) return false;
      return true;
    });
  }

  const remark = String(filter.remark || '').trim().toLowerCase();
  if (remark) {
    list = list.filter((m) => String(m.remark || '').toLowerCase().includes(remark));
  }

  return list.filter((m) => m.mod !== 'D');
}

export function filterObligationsForApprove(masters = [], filter = {}) {
  const st = String(filter.status || '').trim();
  // 상태 미지정 시 승인요청만 (승인화면 기본)
  return filterObligationsForRegister(masters, {
    ...filter,
    status: st || EXPENSE_STATUS.REQUESTED,
  });
}

/** 확정 모드 — 승인 없이 CONFIRMED */
export function confirmObligation(masters, vendors, contracts, obligationUid) {
  const uid = String(obligationUid || '').trim();
  const today = todayYmd();
  const nextM = (masters || []).map((m) => {
    if (m.obligationUid !== uid) return m;
    return {
      ...m,
      status: EXPENSE_STATUS.CONFIRMED,
      confirmDate: today,
    };
  });
  return { masters: nextM, vendors: vendors || [], contracts: contracts || [] };
}

export function requestObligationApproval(masters, vendors, contracts, obligationUid) {
  const uid = String(obligationUid || '').trim();
  const nextM = (masters || []).map((m) => {
    if (m.obligationUid !== uid) return m;
    return {
      ...m,
      status: EXPENSE_STATUS.REQUESTED,
      requestAt: new Date().toISOString(),
    };
  });
  return { masters: nextM, vendors: vendors || [], contracts: contracts || [] };
}

export function approveObligation(masters, vendors, contracts, obligationUid) {
  const uid = String(obligationUid || '').trim();
  const today = todayYmd();
  const nextM = (masters || []).map((m) => {
    if (m.obligationUid !== uid) return m;
    return {
      ...m,
      status: EXPENSE_STATUS.APPROVED,
      approveDate: today,
    };
  });
  return { masters: nextM, vendors: vendors || [], contracts: contracts || [] };
}

export function rejectObligation(masters, vendors, contracts, obligationUid, reason = '반려') {
  const uid = String(obligationUid || '').trim();
  const nextM = (masters || []).map((m) => {
    if (m.obligationUid !== uid) return m;
    return {
      ...m,
      status: EXPENSE_STATUS.REJECTED,
      rejectReason: reason,
    };
  });
  return { masters: nextM, vendors: vendors || [], contracts: contracts || [] };
}

/** 결의 저장 시 원인행위 없으면 stub 생성 */
export function createObligationStubFromResolution(dims, resolutionMaster, existingObligations = []) {
  const oblNo = nextObligationNo(existingObligations);
  const uid = createObligationUid();
  const master = emptyObligationMaster({
    obligationUid: uid,
    obligationNo: oblNo,
    fiscalYear: dims.fiscalYear,
    deptCode: resolutionMaster.deptCode || '',
    deptName: resolutionMaster.deptName || '',
    projectCode: resolutionMaster.projectCode || '',
    projectName: resolutionMaster.projectName || '',
    subjectCode: resolutionMaster.subjectCode || '',
    subjectName: resolutionMaster.subjectName || '',
    fundCode: resolutionMaster.fundCode || '',
    fundName: resolutionMaster.fundName || '',
    balanceUid: resolutionMaster.balanceUid || '',
    obligationAmt: resolutionMaster.resolutionAmt || 0,
    obligationDate: resolutionMaster.resolutionDate || todayYmd(),
    obligationType: OBLIGATION_TYPE.GENERAL,
    status: EXPENSE_STATUS.CONFIRMED,
    confirmDate: todayYmd(),
    registerDate: todayYmd(),
    remark: `결의 ${resolutionMaster.resolutionNo || ''} 자동생성`,
    mod: 'A',
  });
  return master;
}

// ─── Resolution (지출결의) ───────────────────────────────────────

export function emptyResolutionMaster(partial = {}) {
  return {
    resolutionUid: '',
    resolutionNo: '',
    obligationUid: '',
    obligationNo: '',
    fiscalYear: '',
    deptCode: '',
    deptName: '',
    orgName: '',
    projectCode: '',
    projectName: '',
    policyCode: '',
    policyName: '',
    unitCode: '',
    unitName: '',
    detailCode: '',
    detailName: '',
    subjectCode: '',
    subjectName: '',
    gwanCode: '',
    gwanName: '',
    hangCode: '',
    hangName: '',
    sehangCode: '',
    sehangName: '',
    mokCode: '',
    mokName: '',
    semokCode: '',
    semokName: '',
    fundCode: '',
    fundName: '',
    balanceUid: '',
    resolutionAmt: 0,
    payTotalAmt: 0,
    supplyAmt: 0,
    feeAmt: 0,
    resolutionDate: '',
    proposeDate: '',
    contractDate: '',
    inspectDate: '',
    paymentProposeDate: '',
    paymentOrderNo: '',
    payMethod: '',
    receiptDate: '',
    receiptName: '',
    disburseConfirmDate: '',
    processStampJson: {},
    formId: '',
    remark: '',
    resolutionType: RESOLUTION_TYPE.GENERAL,
    status: EXPENSE_STATUS.DRAFT,
    registerDate: '',
    approveDate: '',
    requestAt: '',
    mod: '',
    ...partial,
  };
}

export function emptyResolutionDetailRow(partial = {}) {
  return {
    detailUid: '',
    resolutionUid: '',
    vendorCode: '',
    vendorName: '',
    vendorAddress: '',
    vendorCeoName: '',
    evidenceType: '',
    evidenceNo: '',
    vatYn: 'N',
    vatAmt: 0,
    accountDr: '',
    accountCr: '',
    bankName: '',
    accountNo: '',
    accountHolder: '',
    cardNo: '',
    amount: 0,
    remark: '',
    mod: '',
    ...partial,
  };
}

export async function loadResolutionBundle(dims) {
  if (isBdApiEnabled()) return apiLoadResolutionBundle(dims);
  const bundle = readBundle(storageKey(RESOLUTION_STORAGE_PREFIX, dims), ['details']);
  setCachedResolutionBundle(dims, bundle);
  return bundle;
}

export async function saveResolutionBundle(dims, masters, details) {
  if (isBdApiEnabled()) {
    return apiSaveResolutionBundle(
      dims,
      masters,
      details,
      createResolutionUid,
      createResolutionDetailUid
    );
  }
  writeBundle(storageKey(RESOLUTION_STORAGE_PREFIX, dims), { masters, details });
  return loadResolutionBundle(dims);
}

/** 원인행위번호-순번 형식 */
export function nextResolutionNo(obligationNo, existingResolutions = []) {
  const oblNo = String(obligationNo || '').trim();
  const prefix = `${oblNo}-`;
  const seqs = (existingResolutions || [])
    .filter((r) => String(r.resolutionNo || '').startsWith(prefix))
    .map((r) => {
      const tail = String(r.resolutionNo).slice(prefix.length);
      return Number(tail) || 0;
    })
    .filter((n) => n > 0);
  const next = seqs.length ? Math.max(...seqs) + 1 : 1;
  return `${oblNo}-${next}`;
}

export function filterResolutionsForRegister(masters = [], filter = {}) {
  let list = [...(masters || [])];
  const st = String(filter.status || '').trim();
  if (st) list = list.filter((m) => m.status === st);

  list = matchAxis(list, {
    codeField: 'deptCode',
    nameField: 'deptName',
    codeJoined: filter.deptCode,
    nameJoined: filter.deptName,
  });
  list = matchAxis(list, {
    codeField: 'projectCode',
    nameField: 'projectName',
    codeJoined: filter.projectCode,
    nameJoined: filter.projectName,
  });
  list = matchAxis(list, {
    codeField: 'subjectCode',
    nameField: 'subjectName',
    codeJoined: filter.subjectCode,
    nameJoined: filter.subjectName,
  });

  const dateFrom = toStorageDate(filter.dateFrom);
  const dateTo = toStorageDate(filter.dateTo);
  if (dateFrom || dateTo) {
    list = list.filter((m) => {
      const d = toStorageDate(m.resolutionDate);
      if (!d) return false;
      if (dateFrom && d < dateFrom) return false;
      if (dateTo && d > dateTo) return false;
      return true;
    });
  }

  const remark = String(filter.remark || '').trim().toLowerCase();
  if (remark) {
    list = list.filter((m) => String(m.remark || '').toLowerCase().includes(remark));
  }

  return list.filter((m) => m.mod !== 'D');
}

export function filterResolutionsForApprove(masters = [], filter = {}) {
  const st = String(filter.status || '').trim();
  // 상태 미지정 시 승인요청만 (승인화면 기본)
  return filterResolutionsForRegister(masters, {
    ...filter,
    status: st || EXPENSE_STATUS.REQUESTED,
  });
}

/**
 * 원인행위 없이 결의 저장 시 stub 원인행위 + 결의번호 `{newOblNo}-1` 보장
 * @returns {{ obligationMaster, resolutionMaster, obligationBundle }}
 */
export function ensureObligationForResolution(dims, resolutionMaster, obligationBundle, resolutionBundle) {
  const oblUid = String(resolutionMaster.obligationUid || '').trim();
  let obMasters = [...(obligationBundle?.masters || [])];
  let obVendors = [...(obligationBundle?.vendors || [])];
  let obContracts = [...(obligationBundle?.contracts || [])];
  let master = { ...resolutionMaster };

  if (!oblUid) {
    const stub = createObligationStubFromResolution(dims, master, obMasters);
    obMasters.push(stub);
    master = {
      ...master,
      obligationUid: stub.obligationUid,
      obligationNo: stub.obligationNo,
      resolutionNo: nextResolutionNo(stub.obligationNo, resolutionBundle?.masters || []),
    };
  } else if (!master.resolutionNo) {
    const obl = obMasters.find((o) => o.obligationUid === oblUid);
    const oblNo = obl?.obligationNo || master.obligationNo || '';
    master.resolutionNo = nextResolutionNo(oblNo, resolutionBundle?.masters || []);
    master.obligationNo = oblNo;
  }

  return {
    obligationMaster: obMasters.find((o) => o.obligationUid === master.obligationUid),
    resolutionMaster: master,
    obligationBundle: { masters: obMasters, vendors: obVendors, contracts: obContracts },
  };
}

/** 승인 화면용 — 마스터+디테일 1그리드 평탄화 */
export async function loadJoinedForApprove(dims) {
  const bundle = await loadResolutionBundle(dims);
  const rows = [];
  (bundle.masters || [])
    .filter((m) => m.mod !== 'D' && m.status === EXPENSE_STATUS.REQUESTED)
    .forEach((m) => {
      const dets = (bundle.details || []).filter(
        (d) => d.resolutionUid === m.resolutionUid && d.mod !== 'D'
      );
      if (!dets.length) {
        rows.push({
          ...m,
          statusNm: expenseStatusLabel(m.status),
          resolutionTypeNm: resolutionTypeLabel(m.resolutionType),
        });
        return;
      }
      dets.forEach((d) => {
        rows.push({
          ...m,
          ...d,
          statusNm: expenseStatusLabel(m.status),
          resolutionTypeNm: resolutionTypeLabel(m.resolutionType),
        });
      });
    });
  return rows;
}

export function requestResolutionApproval(masters, details, resolutionUid) {
  const uid = String(resolutionUid || '').trim();
  const nextM = (masters || []).map((m) => {
    if (m.resolutionUid !== uid) return m;
    return {
      ...m,
      status: EXPENSE_STATUS.REQUESTED,
      requestAt: new Date().toISOString(),
    };
  });
  return { masters: nextM, details: details || [] };
}

export function approveResolution(masters, details, resolutionUid) {
  const uid = String(resolutionUid || '').trim();
  const today = todayYmd();
  const nextM = (masters || []).map((m) => {
    if (m.resolutionUid !== uid) return m;
    return {
      ...m,
      status: EXPENSE_STATUS.APPROVED,
      approveDate: today,
    };
  });
  return { masters: nextM, details: details || [] };
}

export function rejectResolution(masters, details, resolutionUid, reason = '반려') {
  const uid = String(resolutionUid || '').trim();
  const nextM = (masters || []).map((m) => {
    if (m.resolutionUid !== uid) return m;
    return {
      ...m,
      status: EXPENSE_STATUS.REJECTED,
      rejectReason: reason,
    };
  });
  return { masters: nextM, details: details || [] };
}

// ─── Payment (지급명령) ──────────────────────────────────────────

export function emptyPaymentRow(partial = {}) {
  return {
    paymentUid: '',
    resolutionUid: '',
    resolutionNo: '',
    fiscalYear: '',
    deptCode: '',
    deptName: '',
    projectCode: '',
    projectName: '',
    subjectCode: '',
    subjectName: '',
    fundCode: '',
    fundName: '',
    payAmt: 0,
    payDate: '',
    orgAccount: '',
    vendorAccount: '',
    depositMemo: '',
    withdrawMemo: '',
    remark: '',
    paidYn: 'N',
    paidAt: '',
    cmsSentYn: 'N',
    mod: '',
    ...partial,
  };
}

export async function loadPayments(dims) {
  if (isBdApiEnabled()) return apiLoadPayments(dims);
  try {
    const raw = localStorage.getItem(storageKey(PAYMENT_STORAGE_PREFIX, dims));
    if (!raw) {
      setCachedPayments(dims, []);
      return [];
    }
    const data = JSON.parse(raw);
    const rows = Array.isArray(data?.payments) ? data.payments : [];
    setCachedPayments(dims, rows);
    return rows;
  } catch {
    setCachedPayments(dims, []);
    return [];
  }
}

export async function savePayments(dims, payments) {
  if (isBdApiEnabled()) {
    return apiSavePayments(dims, payments, createPaymentUid);
  }
  writeBundle(storageKey(PAYMENT_STORAGE_PREFIX, dims), { payments: payments || [] });
  return loadPayments(dims);
}

/** 승인된 결의 중 미지급 후보 + 기존 지급행 병합 */
export async function loadPaymentCandidates(dims) {
  const resBundle = await loadResolutionBundle(dims);
  const existing = await loadPayments(dims);
  const paidResUids = new Set(
    (existing || []).filter((p) => p.paidYn === 'Y').map((p) => p.resolutionUid)
  );

  const approved = (resBundle.masters || []).filter(
    (m) => m.mod !== 'D' && m.status === EXPENSE_STATUS.APPROVED && !paidResUids.has(m.resolutionUid)
  );

  const candidates = approved.map((m) => {
    const prev = (existing || []).find((p) => p.resolutionUid === m.resolutionUid);
    if (prev) return { ...prev, ...m, payAmt: prev.payAmt || m.resolutionAmt || 0 };
    return emptyPaymentRow({
      paymentUid: createPaymentUid(),
      resolutionUid: m.resolutionUid,
      resolutionNo: m.resolutionNo,
      fiscalYear: m.fiscalYear || dims.fiscalYear,
      deptCode: m.deptCode,
      deptName: m.deptName,
      projectCode: m.projectCode,
      projectName: m.projectName,
      subjectCode: m.subjectCode,
      subjectName: m.subjectName,
      fundCode: m.fundCode,
      fundName: m.fundName,
      payAmt: m.resolutionAmt || 0,
      payDate: todayYmd(),
      mod: 'A',
    });
  });

  return candidates;
}

export async function confirmPayment(dims, paymentRows, resolutionUids, options = {}) {
  const uidSet = new Set((resolutionUids || []).map(String));
  const now = new Date().toISOString();
  const today = todayYmd();
  const allExisting = await loadPayments(dims);
  const byRes = new Map(allExisting.map((p) => [p.resolutionUid, p]));

  (paymentRows || []).forEach((row) => {
    if (!uidSet.has(String(row.resolutionUid))) return;
    const prev = byRes.get(row.resolutionUid) || {};
    byRes.set(row.resolutionUid, {
      ...prev,
      ...row,
      paidYn: 'Y',
      paidAt: now,
      payDate: row.payDate || today,
      cmsSentYn: options.cmsSent ? 'Y' : row.cmsSentYn || 'N',
      mod: prev.paymentUid ? 'U' : 'A',
      paymentUid: row.paymentUid || prev.paymentUid || createPaymentUid(),
    });
  });

  return savePayments(dims, Array.from(byRes.values()));
}
